const TOKEN_KEY = "bb_token";

export const AUTH_EXPIRED_EVENT = "bb:auth-expired";

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown, message: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  return parseResponse<T>(res, method, path);
}

async function requestMultipart<T>(path: string, form: FormData): Promise<T> {
  const headers: HeadersInit = {};
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(path, { method: "POST", headers, body: form });
  return parseResponse<T>(res, "POST", path);
}

async function parseResponse<T>(res: Response, method: string, path: string): Promise<T> {
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const detail = isJson && typeof payload === "object" && payload && "detail" in payload
      ? (payload as { detail: string }).detail
      : `${method} ${path} failed (${res.status})`;
    if (res.status === 401 && sessionStorage.getItem(TOKEN_KEY)) {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail }));
    }
    throw new ApiError(res.status, payload, detail);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
  postMultipart: <T>(path: string, form: FormData) => requestMultipart<T>(path, form),
};

// === Domain types — match backend Pydantic schemas ===

export type Country = "PL" | "UK";
export type Locale = "pl-PL" | "en-GB";

export type Tenant = {
  id: number;
  email: string;
  display_name: string | null;
  plan: string;
  max_stores: number;
  monthly_transaction_limit: number;
  monthly_fee: string;
  license_status: string | null;
  license_expires_at: string | null;
  country: Country;
  locale: Locale;
};

export type Store = {
  id: number;
  name: string;
  city: string | null;
  created_at: string;
};

export type KPI = {
  period_days: number;
  transactions: number;
  avg_basket: string;
  items_per_basket: number;
  gross_margin_pct: number;
  source: "transactions" | "aggregate" | "empty";
};

export type Bestseller = {
  sku: string;
  name: string;
  category: string | null;
  receipts_count: number | null;
  support_pct: string | null;
  quantity_sold: string | null;
  daily_velocity: string | null;
  total_revenue_net: string | null;
  avg_basket_value: string | null;
};

export type MBAPair = {
  antecedent_sku: string;
  consequent_sku: string;
  support: number;
  confidence: number;
  lift: number;
  co_occurrences: number;
};

export type BundleProduct = {
  sku: string;
  name: string;
  avg_price: string;
  avg_margin_pct: string | null;
};

export type BundleProposal = {
  products: BundleProduct[];
  bundle_total_price: string;
  expected_aov_uplift: number;
  bundle_margin_pct: string;
  base_avg_margin_pct: string;
  margin_ratio: number;
  support: number;
  lift: number;
};

export type DataImport = {
  id: number;
  store_id: number;
  file_name: string;
  symplex_format: string;
  status: "PENDING" | "PROCESSING" | "DONE" | "ERROR";
  rows_total: number | null;
  rows_processed: number | null;
  date_from: string | null;
  date_to: string | null;
  error_log: string | null;
  created_at: string;
  processed_at: string | null;
};

export type ImportSummary = {
  import_id: number;
  rows_processed: number;
  transactions_created: number;
  products_created: number;
  products_updated: number;
  alerts_generated: number;
  warnings: string[];
  errors: string[];
};

export type AlertType =
  | "DEAD_STOCK"
  | "LOW_MARGIN_BESTSELLER"
  | "HIGH_AFFINITY_OPPORTUNITY"
  | "STOCKOUT_RISK"
  | "VELOCITY_SPIKE"
  | "SEASONAL_OPPORTUNITY"
  | "ANOMALY";

export type AlertSeverity = "INFO" | "WARNING" | "CRITICAL";

export type Alert = {
  id: number;
  store_id: number;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  body: string;
  sku: string | null;
  is_read: boolean;
  created_at: string;
};

export type PurchaseUrgency = "ZAM_TERAZ" | "ZAM_TYDZIEN" | "OK";

export type PurchaseRecommendation = {
  sku: string;
  name: string;
  daily_velocity: string | null;
  qty_suggested_7d: string | null;
  current_stock_days: number | null;
  urgency: PurchaseUrgency;
  reason_pl: string;
};

export type AOVTrendPoint = {
  date: string;
  avg_basket: string;
  transactions: number;
};

export type AOVTrend = {
  period_days: number;
  points: AOVTrendPoint[];
  source: "transactions" | "empty";
};

export type UpsellPotential = {
  bundles_count: number;
  monthly_uplift_pln: string;
  baseline_aov: string;
  explanation_pl: string;
};

// === Endpoint helpers ===

export const endpoints = {
  me: () => api.get<Tenant>("/api/me"),
  updateRegion: (country: Country, locale: Locale) =>
    api.patch<Tenant>("/api/me/region", { country, locale }),
  stores: () => api.get<Store[]>("/api/stores"),
  createStore: (name: string, city?: string) =>
    api.post<Store>("/api/stores", { name, city }),
  kpi: (storeId: number, days = 30) =>
    api.get<KPI>(`/api/analytics/kpi?store_id=${storeId}&days=${days}`),
  mba: (storeId: number, days = 30, limit = 50) =>
    api.get<MBAPair[]>(`/api/analytics/mba?store_id=${storeId}&days=${days}&limit=${limit}`),
  bundles: (storeId: number, days = 30, limit = 20) =>
    api.get<BundleProposal[]>(
      `/api/analytics/bundles?store_id=${storeId}&days=${days}&limit=${limit}`,
    ),
  bestsellers: (storeId: number, limit = 20) =>
    api.get<Bestseller[]>(`/api/analytics/bestsellers?store_id=${storeId}&limit=${limit}`),
  imports: (storeId?: number, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (storeId !== undefined) params.set("store_id", String(storeId));
    return api.get<DataImport[]>(`/api/imports?${params.toString()}`);
  },
  uploadCSV: (file: File, storeId: number) => {
    const form = new FormData();
    form.append("file", file);
    form.append("store_id", String(storeId));
    return api.postMultipart<ImportSummary>("/api/imports/upload", form);
  },
  alerts: (storeId?: number, unread = true, limit = 10) => {
    const params = new URLSearchParams({ limit: String(limit), unread: String(unread) });
    if (storeId !== undefined) params.set("store_id", String(storeId));
    return api.get<Alert[]>(`/api/alerts?${params.toString()}`);
  },
  markAlertRead: (alertId: number) =>
    api.patch<Alert>(`/api/alerts/${alertId}/read`, {}),
  purchaseRecommendations: (storeId: number, limit = 20) =>
    api.get<PurchaseRecommendation[]>(
      `/api/analytics/purchase-recommendations?store_id=${storeId}&limit=${limit}`,
    ),
  aovTrend: (storeId: number, days = 30) =>
    api.get<AOVTrend>(`/api/analytics/aov-trend?store_id=${storeId}&days=${days}`),
  upsellPotential: (storeId: number, days = 30) =>
    api.get<UpsellPotential>(`/api/analytics/upsell-potential?store_id=${storeId}&days=${days}`),
};
